<script lang="ts">
  import { onMount } from "svelte";

  import { api, ApiError, type Entitlements } from "../../lib/api";
  import { beginLogin, isSignedIn } from "../../lib/auth";
  import { resolveAppConfig } from "../../lib/config";
  import { go } from "../../lib/router.svelte";

  let ent = $state<Entitlements | null>(null);
  let error = $state("");
  let loading = $state(true);
  let busyPortal = $state(false);

  onMount(async () => {
    if (!isSignedIn()) {
      const cfg = await resolveAppConfig();
      await beginLogin(cfg.oidc, "/account");
      return;
    }
    try {
      ent = await api.entitlements();
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Failed to load account";
    } finally {
      loading = false;
    }
  });

  function fmtDate(iso: string | null): string {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString();
  }

  /** Open the Paddle customer portal — for cancel / change payment method /
   *  switch plan / view invoices. The cloud creates a single-use URL per
   *  call (it never persists a portal URL — Paddle expires them). */
  async function openPortal() {
    if (busyPortal) return;
    busyPortal = true;
    error = "";
    try {
      const { portal_url } = await api.portal();
      window.location.assign(portal_url);
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not open billing portal";
      busyPortal = false;
    }
  }
</script>

<section class="max-w-3xl mx-auto px-6 py-16">
  <h1 class="text-3xl font-bold">Account</h1>

  {#if loading}
    <p class="mt-6 text-slate-400">Loading…</p>
  {:else if error}
    <p class="mt-6 text-red-400">{error}</p>
  {:else if ent}
    <div class="mt-6 rounded-xl border border-white/10 p-6">
      <div class="flex items-center gap-3">
        <span class="text-2xl font-semibold capitalize">{ent.plan}</span>
        <span class="text-xs px-2 py-0.5 rounded-full bg-white/10 text-slate-300">
          {ent.source}
        </span>
      </div>
      {#if ent.source === "group"}
        <p class="mt-2 text-sm text-slate-400">
          Granted by your access group. Contact your administrator to change.
        </p>
      {:else if ent.source === "manual"}
        <p class="mt-2 text-sm text-slate-400">
          Granted by your administrator{ent.valid_until ? ` until ${fmtDate(ent.valid_until)}` : ""}.
        </p>
      {:else if ent.source === "promo" && ent.valid_until}
        <p class="mt-2 text-sm text-amber-300">
          Pro promo active until {fmtDate(ent.valid_until)}
        </p>
      {:else if ent.source === "paid" && ent.scheduled_change_action === "cancel" && ent.scheduled_change_effective_at}
        <p class="mt-2 text-sm text-amber-300">
          Cancels on {fmtDate(ent.scheduled_change_effective_at)} — access continues until then
        </p>
      {:else if ent.source === "paid" && ent.scheduled_change_action === "change_plan" && ent.scheduled_change_effective_at}
        <p class="mt-2 text-sm text-amber-300">
          Plan change scheduled for {fmtDate(ent.scheduled_change_effective_at)} — see Manage subscription for details
        </p>
      {:else if ent.source === "paid" && ent.scheduled_change_action === "pause" && ent.scheduled_change_effective_at}
        <p class="mt-2 text-sm text-amber-300">
          Pauses on {fmtDate(ent.scheduled_change_effective_at)}
        </p>
      {:else if ent.source === "paid" && ent.renews_at && ent.cancel_at_period_end}
        <p class="mt-2 text-sm text-amber-300">
          Ends on {fmtDate(ent.renews_at)} — won't auto-renew
        </p>
      {:else if ent.source === "paid" && ent.renews_at}
        <p class="mt-2 text-sm text-slate-400">
          Renews on {fmtDate(ent.renews_at)}
        </p>
      {:else if ent.valid_until}
        <p class="mt-2 text-sm text-slate-400">Access until {fmtDate(ent.valid_until)}</p>
      {/if}

      <ul class="mt-4 text-sm text-slate-300 space-y-1">
        <li>Remote control: {ent.features.remote_control ? "✓" : "—"}</li>
        <li>Memory / RAG: {ent.features.memory_rag ? "✓" : "—"}</li>
        <li>Advanced tools: {ent.features.advanced_tools ? "✓" : "—"}</li>
        <li>Workflows: {ent.features.workflows ? "✓" : "—"}</li>
        <li>Priority models: {ent.features.priority_models ? "✓" : "—"}</li>
      </ul>

      <div class="mt-6 flex flex-wrap gap-3 text-sm">
        {#if ent.source === "paid"}
          <!-- Cancel / change card / switch plan / invoices live in Paddle's
               customer portal — the cloud only mints the entry URL. -->
          <button
            onclick={openPortal}
            disabled={busyPortal}
            class="px-4 py-2 rounded-md bg-indigo-500 hover:bg-indigo-400 text-white disabled:opacity-50"
          >
            {busyPortal ? "Opening…" : "Manage subscription"}
          </button>
        {/if}
        {#if ent.plan === "basic"}
          <a
            href="/redeem"
            onclick={(e) => go(e, "/redeem")}
            class="px-4 py-2 rounded-md bg-indigo-500 hover:bg-indigo-400 text-white"
          >
            Redeem a voucher
          </a>
          <a
            href="/pricing"
            onclick={(e) => go(e, "/pricing")}
            class="px-4 py-2 rounded-md border border-white/15 hover:border-white/30"
          >
            View plans
          </a>
        {/if}
        <a
          href="/account/usage"
          onclick={(e) => go(e, "/account/usage")}
          class="px-4 py-2 rounded-md border border-white/15 hover:border-white/30"
        >
          Usage
        </a>
      </div>
    </div>
  {/if}
</section>
